import { useId, useLayoutEffect, useRef, type ReactNode } from "react";
import { X } from "lucide-react";

/** Native modal semantics provide focus containment, inert background and return. */
export function Dialog({
    open,
    title,
    children,
    onClose,
}: {
    open: boolean;
    title: string;
    children: ReactNode;
    onClose: () => void;
}) {
    const ref = useRef<HTMLDialogElement>(null);
    const titleId = useId();
    useLayoutEffect(() => {
        const dialog = ref.current;
        if (open && dialog && !dialog.open) dialog.showModal();
        if (!open && dialog?.open) dialog.close();
        // Conditional callers unmount the dialog on dismissal. Close before
        // DOM removal so native focus restoration can reach the opener.
        return () => { if (dialog?.open) dialog.close(); };
    }, [open]);
    return (
        <dialog
            ref={ref}
            className="app-dialog"
            aria-labelledby={titleId}
            onCancel={(event) => {
                event.preventDefault();
                onClose();
            }}
            onClick={(event) => {
                if (event.target === event.currentTarget) onClose();
            }}
        >
            <div className="p-6 sm:p-8">
                <div className="mb-5 flex items-center justify-between gap-4">
                    <h2 id={titleId} className="text-xl font-semibold">
                        {title}
                    </h2>
                    <button
                        type="button"
                        className="icon-button"
                        aria-label="Close dialog"
                        onClick={onClose}
                    >
                        <X size={18} />
                    </button>
                </div>
                {children}
            </div>
        </dialog>
    );
}
